﻿import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Card,
  Empty,
  Flex,
  Grid,
  List,
  Segmented,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { AdminOrder } from '@titularis/api-client';
import { useEstadoDeLista } from '@titularis/ui';
import { api } from '../../api';
import {
  condicaoDoPreco,
  emReais,
  formatarData,
  FORMA_LABEL,
  STATUS_PEDIDO_COR,
  STATUS_PEDIDO_LABEL,
} from '../../dominio';
import { rotuloDoCanal } from '../origem';
import { GavetaDoPedido } from './GavetaDoPedido';

/**
 * O histórico de vendas de um produto.
 *
 * **É a tela de suporte.** Quem chega aqui está respondendo a alguém que disse
 * "paguei e não liberou", e a resposta mora em três coisas: a situação do
 * pedido, quando ele foi pago e o que o provedor mandou. As duas primeiras
 * ficam na lista; a terceira abre na gaveta.
 *
 * O filtro de situação vive na URL, como nas demais listagens: "os estornos
 * deste produto" é um link que se manda para outra pessoa.
 */
const POR_PAGINA = 20;

/**
 * Os recortes que alguém de fato pede.
 *
 * Não é a lista inteira de situações de propósito: cancelado e expirado são o
 * mesmo assunto - checkout que não virou venda -, e uma fila de seis botões
 * espremeria os dois que importam.
 */
const RECORTES = [
  { label: 'Tudo', value: 'tudo' },
  { label: 'Pagos', value: 'pago' },
  { label: 'Em aberto', value: 'criado' },
  { label: 'Devolvidos', value: 'estornado' },
] as const;

export function PedidosDoProduto({ productId }: { productId: string }) {
  const telas = Grid.useBreakpoint();
  const compacto = !telas.md;
  const { ler, filtrar, irParaPagina } = useEstadoDeLista();
  const [aberto, setAberto] = useState<string | null>(null);

  const recorte = RECORTES.find((opcao) => opcao.value === ler.texto('situacao'))?.value ?? 'tudo';
  const page = ler.pagina();

  const pedidos = useQuery({
    queryKey: ['pedidos', { productId, recorte, page }],
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.listOrders({
        productId,
        ...(recorte === 'tudo' ? {} : { status: recorte }),
        page,
        pageSize: POR_PAGINA,
      }),
  });

  const comprador = (pedido: AdminOrder) =>
    pedido.comprador.nome ? (
      <Flex vertical gap={0} style={{ minWidth: 0 }}>
        <Typography.Text ellipsis>{pedido.comprador.nome}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
          {pedido.comprador.email}
        </Typography.Text>
      </Flex>
    ) : (
      // Conta excluída: o pedido fica, e dizer isso é melhor que um id nu.
      <Tooltip title={`Conta excluída. Id: ${pedido.comprador.id}`}>
        <Typography.Text type="secondary">conta excluída</Typography.Text>
      </Tooltip>
    );

  /*
   * A etiqueta de ensaio anda junto da situação porque é a mesma pergunta: um
   * "Pago" de sandbox parece uma venda e não é. Ela some sozinha quando o
   * `encerrar-sandbox.ts` não tiver mais o que carimbar.
   */
  const etiqueta = (pedido: AdminOrder) => (
    <Flex gap={4} wrap>
      <Tag color={STATUS_PEDIDO_COR[pedido.status]} style={{ marginInlineEnd: 0 }}>
        {STATUS_PEDIDO_LABEL[pedido.status]}
      </Tag>
      {pedido.sandbox && (
        <Tooltip title="Nasceu no ambiente de teste do provedor. Aparece no histórico e não soma dinheiro.">
          <Tag style={{ marginInlineEnd: 0 }}>ensaio</Tag>
        </Tooltip>
      )}
    </Flex>
  );

  /*
   * **O ensaio também se marca onde o dinheiro é lido.**
   *
   * A etiqueta ao lado da situação já existia, e não bastou: o olho procura
   * dinheiro na coluna de Valor, e um "R$ 588,00" continuava lendo como
   * cobrança que aconteceu - o Calebe leu assim em 17/09/2026, depois da
   * primeira venda de verdade, e perguntou de onde tinham saído aquelas
   * cobranças. Riscado e em texto secundário, o valor de ensaio para de
   * competir com a venda real na mesma lista.
   */
  const valor = (pedido: AdminOrder) =>
    pedido.sandbox ? (
      <Tooltip title="Dinheiro de mentira: nasceu no ambiente de teste do provedor e não entra na receita.">
        <Typography.Text delete type="secondary">
          {emReais(pedido.amountCents)}
        </Typography.Text>
      </Tooltip>
    ) : (
      <Typography.Text>{emReais(pedido.amountCents)}</Typography.Text>
    );

  /*
   * É o canal congelado na venda, não o primeiro toque da conta. A ausência
   * fica em texto secundário: uma etiqueta a faria parecer uma categoria.
   * Tabela e cartões usam a mesma leitura para a origem continuar no celular.
   */
  const origemDoPedido = (pedido: AdminOrder) => {
    const origem = pedido.origem;
    const temCanal = origem?.source || origem?.medium || origem?.campaign;
    return (
      <Typography.Text type={temCanal ? undefined : 'secondary'}>
        {rotuloDoCanal(origem)}
      </Typography.Text>
    );
  };

  const colunas = [
    { title: 'Comprador', key: 'comprador', render: (_: unknown, l: AdminOrder) => comprador(l) },
    {
      title: 'Origem',
      key: 'origem',
      width: 240,
      ellipsis: true,
      render: (_: unknown, pedido: AdminOrder) => origemDoPedido(pedido),
    },
    {
      title: 'Forma',
      key: 'forma',
      render: (_: unknown, linha: AdminOrder) => (
        <Flex vertical gap={0}>
          <Typography.Text>{FORMA_LABEL[linha.forma] ?? linha.forma}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {condicaoDoPreco(linha.amountCents, linha.maxInstallments)}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Valor',
      key: 'valor',
      align: 'right' as const,
      render: (_: unknown, linha: AdminOrder) => valor(linha),
    },
    {
      title: 'Situação',
      key: 'status',
      render: (_: unknown, linha: AdminOrder) => etiqueta(linha),
    },
    {
      /*
       * As duas datas, e cada uma com o seu nome. A versão anterior mostrava
       * uma só com "pago" ou "aberto" embaixo, e num pedido expirado o rótulo
       * "aberto" contradizia a coluna ao lado, que dizia "Expirado".
       */
      title: 'Quando',
      key: 'quando',
      render: (_: unknown, linha: AdminOrder) => (
        <Flex vertical gap={0}>
          <Typography.Text style={{ fontSize: 12 }}>
            Aberto {formatarData(linha.createdAt)}
          </Typography.Text>
          {linha.paidAt && (
            <Typography.Text style={{ fontSize: 12 }}>
              Pago {formatarData(linha.paidAt)}
            </Typography.Text>
          )}
        </Flex>
      ),
    },
  ];

  const vazio = (
    <Empty
      description={
        recorte === 'tudo'
          ? 'Nenhuma venda ainda.'
          : 'Nenhum pedido com esse recorte.'
      }
    />
  );

  return (
    <Flex vertical gap={12}>
      <Flex justify="space-between" align="center" wrap gap={12}>
        <Segmented
          value={recorte}
          onChange={(valor) =>
            filtrar({ situacao: valor === 'tudo' ? undefined : String(valor) })
          }
          options={[...RECORTES]}
        />
        {pedidos.data && pedidos.data.total > 0 && (
          <Typography.Text type="secondary">
            {pedidos.data.total} pedido(s) · {emReais(pedidos.data.receitaCents)} pagos
          </Typography.Text>
        )}
      </Flex>

      {compacto ? (
        <List
          loading={pedidos.isLoading}
          dataSource={pedidos.data?.items ?? []}
          locale={{ emptyText: vazio }}
          pagination={{
            current: pedidos.data?.page ?? page,
            pageSize: POR_PAGINA,
            total: pedidos.data?.total ?? 0,
            showSizeChanger: false,
            onChange: (pagina: number) => irParaPagina(pagina),
          }}
          renderItem={(pedido) => (
            <List.Item style={{ paddingInline: 0 }}>
              <Card size="small" style={{ width: '100%' }} onClick={() => setAberto(pedido.id)}>
                <Flex vertical gap={6} style={{ minWidth: 0 }}>
                  <Flex justify="space-between" align="flex-start" gap={8}>
                    {comprador(pedido)}
                    {etiqueta(pedido)}
                  </Flex>
                  <Typography.Text>
                    {valor(pedido)}{' '}
                    <Typography.Text type="secondary">
                      · {FORMA_LABEL[pedido.forma] ?? pedido.forma}
                    </Typography.Text>
                  </Typography.Text>
                  <div>
                    <Typography.Text type="secondary">Origem: </Typography.Text>
                    {origemDoPedido(pedido)}
                  </div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Aberto {formatarData(pedido.createdAt)}
                    {pedido.paidAt && ` · Pago ${formatarData(pedido.paidAt)}`}
                  </Typography.Text>
                </Flex>
              </Card>
            </List.Item>
          )}
        />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            dataSource={pedidos.data?.items ?? []}
            columns={colunas}
            loading={pedidos.isLoading}
            locale={{ emptyText: vazio }}
            onRow={(linha) => ({
              onClick: () => setAberto(linha.id),
              style: { cursor: 'pointer' },
            })}
            pagination={{
              current: pedidos.data?.page ?? page,
              pageSize: POR_PAGINA,
              total: pedidos.data?.total ?? 0,
              showSizeChanger: false,
              onChange: (pagina: number) => irParaPagina(pagina),
            }}
            scroll={{ x: 1000 }}
          />
        </Card>
      )}

      <GavetaDoPedido pedidoId={aberto} aoFechar={() => setAberto(null)} />
    </Flex>
  );
}
